import assert from '@/sui/tools/assert'
import ctool from 'color'
import options from '@/sui/tools/chart/options'

import {
  dynamic
} from '@/sui/tools/chart/formatter'

function getIcon (opt) {
  assert.notNull(opt)

  const prefix = opt.percentBars ? 'p' : ''
  const suffix = opt.splitBars ? 'split' : ''
  return `${prefix}bar${suffix}`
}

function getTotals (datasets) {
  assert.notNull(datasets)
  assert.isArray(datasets)
  const totals = {}

  for (let i = 0; i !== datasets.length; ++i) {
    const datasetData = datasets[i].data

    for (let j = 0; j !== datasetData.length; ++j) {
      const value = datasetData[j]

      if (totals[j] == null) {
        totals[j] = value
      } else {
        totals[j] += value
      }
    }
  }
  return totals
}

function computePercentDatasets (datasets) {
  assert.notNull(datasets)
  assert.isArray(datasets)

  const result = []
  const totals = getTotals(datasets)

  const datasetsCount = datasets.length
  for (let i = 0; i !== datasetsCount; ++i) {
    const dataset = datasets[i]
    const data = dataset.data

    const percentValues = []
    const counts = []
    for (let j = 0; j !== data.length; ++j) {
      const value = data[j]
      const total = totals[j]
      if (value == null || total == null || total === 0) {
        percentValues.push(null)
        counts.push(null)
        continue
      }

      percentValues.push(value / total * 100)
      counts.push(value)
    }

    result.push({
      backgroundColor: dataset.backgroundColor,
      data: percentValues,
      label: dataset.label,
      counts: counts
    })
  }

  assert.notNull(result)
  assert.isArray(result)
  assert.equals(result.length, datasets.length)
  return result
}

function classic (id, title, datasets, labels, opt = {}) {
  assert.notNull(id)
  assert.notNull(title)
  assert.notNull(datasets)
  assert.notNull(labels)
  assert.notNull(opt)

  const exportConfig = [
    options.titleFontSize(12),
    options.title,
    options.datalabelFontSize(),
    options.axesFontSize
  ]

  let formatter = dynamic.percent.formatter
  if (!opt.percentBars) {
    formatter = dynamic
    const formatters = [
      dynamic.numbers,
      dynamic.computedPercent,
      dynamic.computedPercentWithValue
    ]
    exportConfig.push(
      options.selectFormatter(formatters, opt.defaultFormatter)
    )
  }

  const isEvolution = datasets.length > 1
  if (isEvolution) {
    exportConfig.push(
      options.hideLegend,
      options.legendOnRight,
      options.legendBoxWidth,
      options.legendFontSize
    )
  }

  const icon = getIcon(opt)

  const yAxes = [
    {
      ticks: { min: 0 },
      stacked: !opt.splitBars
    }
  ]

  let finalDatasets = datasets
  if (opt.percentBars) {
    yAxes[0].ticks.max = 100
    finalDatasets = computePercentDatasets(datasets)
  }

  exportConfig.push(options.hideDatalabelBelowPercent)
  if (!opt.percentBars) {
    exportConfig.push(options.hideDatalabelBelowValue)
  }
  exportConfig.push(options.maxYValue(`Valeur maximale`))

  return {
    id: id,
    type: 'bar',
    icon: icon,
    config: {
      data: {
        labels: labels,
        datasets: finalDatasets
      },
      options: {
        title: {
          display: true,
          text: title
        },
        legend: {
          display: isEvolution
        },
        scales: {
          yAxes: yAxes,
          xAxes: [{ stacked: !opt.splitBars }]
        },
        plugins: {
          datalabels: {
            color: '#ffffff',
            textAlign: 'center',
            font: { weight: 'bold' },
            offset: 0,
            formatter: formatter
          }
        }
      }
    },
    exportConfig: exportConfig
  }
}

function indicator (id, title, datasets, labels) {
  assert.isString(id)
  assert.isString(title)
  assert.isArray(datasets)
  assert(datasets.length > 0)
  assert.isArray(labels)

  const finalDatasets = computePercentDatasets(datasets)[0]
  const width = 100 * finalDatasets.data.length
  const height = 100 + finalDatasets.data.length * 8.8
  const expectedRatio = width / height

  return {
    id: id,
    type: 'bar',
    icon: 'indicator-bar',
    expectedRatio: expectedRatio,
    config: {
      data: {
        labels: labels,
        datasets: [finalDatasets]
      },

      options: {
        title: {
          display: true,
          text: title
        },
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              ticks: { min: 0, max: 100 },
              stacked: true,
              display: false
            }
          ],
          xAxes: [
            {
              stacked: true,
              barPercentage: 1.2
            }
          ]
        },

        plugins: {
          datalabels: {
            color: '#ffffff',
            textAlign: 'center',
            font: { weight: 'bold' },
            borderRadius: 3,
            padding: {
              top: 4,
              bottom: 2,
              right: 4,
              left: 4
            },
            anchor: 'end',
            align: 'center',
            offset: 0,
            formatter: dynamic.percentIndicator.formatter,

            backgroundColor: function (context) {
              const index = context.dataIndex
              const value = context.dataset.data[index]
              if (value == null || value.p < 0) {
                return false
              }

              const c = value >= 90 ? '#1cb863'
                : value >= 80 ? ctool('#36c075').mix(ctool('#1cb863'), (value - 80) * 0.1)
                  : ctool('#ff0d0d').mix(ctool('#ffB4B4'), value / 80.0)
              return ctool(c).fade(0.05).hsl().string()
            }
          },
          chartAreaBorder: {
            borderColor: '#f9bc07',
            borderWidth: 2,
            borderDash: [],
            borderDashOffset: 0
          }
        }
      }
    },

    plugins: [
      options.chartAreaBorder
    ],
    exportConfig: [
      options.titleFontSize(15),
      options.title,
      options.datalabelFontSize(17),
      options.axesFontSize
    ]
  }
}

export default {
  classic,
  indicator
}
